import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'

const PAGE_SIZE = 12
const SLOTS_BEFORE_CENTERED_SLOT = 6

interface UseTimeSlotPagerReturn {
  canGoEarlier: ComputedRef<boolean>
  canGoLater: ComputedRef<boolean>
  centerOnSlotAt: (index: number) => void
  goEarlier: () => void
  goLater: () => void
  pageSlots: ComputedRef<string[]>
  resetToFirstPage: () => void
}

/** Pages through `slots` a fixed number at a time and keeps the page inside the list as it changes. */
export const useTimeSlotPager = (
  slots: Readonly<Ref<readonly string[]>>
): UseTimeSlotPagerReturn => {
  const offset = ref(0)

  const pageSlots = computed(() => slots.value.slice(offset.value, offset.value + PAGE_SIZE))
  const canGoEarlier = computed(() => offset.value > 0)
  const canGoLater = computed(() => offset.value + PAGE_SIZE < slots.value.length)

  const goEarlier = () => {
    if (canGoEarlier.value) offset.value = Math.max(0, offset.value - PAGE_SIZE)
  }

  const goLater = () => {
    if (canGoLater.value) {
      offset.value = Math.min(slots.value.length - PAGE_SIZE, offset.value + PAGE_SIZE)
    }
  }

  const resetToFirstPage = () => {
    offset.value = 0
  }

  /** Scrolls so the slot at `index` sits near the middle of the page. */
  const centerOnSlotAt = (index: number) => {
    offset.value = Math.max(0, index - SLOTS_BEFORE_CENTERED_SLOT)
  }

  watch(
    slots,
    (current) => {
      if (offset.value >= current.length) offset.value = Math.max(0, current.length - PAGE_SIZE)
    },
    { immediate: true }
  )

  return {
    canGoEarlier,
    canGoLater,
    centerOnSlotAt,
    goEarlier,
    goLater,
    pageSlots,
    resetToFirstPage
  }
}
